import * as React from "react";
import clsx from "clsx";
import { Modal } from "../Modal";
import { Text } from "../Text";
import { Button } from "../Button";
import * as s from "./LanguagePickerModal.css";

export interface LanguagePickerOption {
  id: string;
  code: string;
  name: string;
  flagUrl?: string | null;
}

export interface LanguagePickerModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  languages: LanguagePickerOption[];
  currentCode: string;
  title: string;
  closeLabel: string;
  onSelect: (code: string) => void | Promise<void>;
}

export function LanguagePickerModal({
  open,
  setOpen,
  languages,
  currentCode,
  title,
  closeLabel,
  onSelect,
}: LanguagePickerModalProps) {
  const [pending, setPending] = React.useState<string | null>(null);

  const handleSelect = async (code: string) => {
    if (code === currentCode) {
      setOpen(false);
      return;
    }
    setPending(code);
    try {
      await onSelect(code);
    } finally {
      setPending(null);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen} minWidth="360px" maxWidth="420px">
      <Modal.Header>
        <Text level="h3" weight="bold">
          {title}
        </Text>
      </Modal.Header>
      <Modal.Body>
        <div className={s.list} role="listbox" aria-label={title}>
          {languages.map((lang) => (
            <button
              key={lang.id}
              type="button"
              role="option"
              aria-selected={lang.code === currentCode}
              className={clsx(s.option, lang.code === currentCode && s.optionActive)}
              disabled={pending !== null}
              onClick={() => void handleSelect(lang.code)}
            >
              {lang.flagUrl ? (
                <img src={lang.flagUrl} alt="" className={s.flag} />
              ) : (
                <span className={s.flagPlaceholder} aria-hidden />
              )}
              <span className={s.meta}>
                <span className={s.name}>{lang.name}</span>
                <span className={s.code}>{lang.code}</span>
              </span>
            </button>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="ghost" color="neutral" onClick={() => setOpen(false)}>
          {closeLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
